/**
 * KioskConfirmation - Order confirmation screen
 * Shows different messages for paid (M-PAiSA) vs pending (Cash) orders
 * Auto-resets after 15 seconds with visible countdown
 */
import { useState, useEffect, useRef } from 'react';
import { CheckCircle, Gift, Clock, Banknote, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk } from './KioskContext';

export default function KioskConfirmation() {
  const {
    isAccessible, buttonSizeClass, iconClass,
    orderNumber, settings, customer, finalTotal, resetKiosk, paymentStatus
  } = useKiosk();

  // Countdown timer state
  const [countdown, setCountdown] = useState(15);
  const timerRef = useRef(null);
  const hasResetRef = useRef(false);

  // Auto-reset countdown effect
  useEffect(() => {
    // Reset countdown when component mounts
    setCountdown(15);
    hasResetRef.current = false;

    // Start countdown timer
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Clear interval and reset kiosk
          clearInterval(timerRef.current);
          if (!hasResetRef.current) {
            hasResetRef.current = true;
            console.log('Kiosk auto-reset triggered after countdown');
            resetKiosk();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [resetKiosk]);

  // Check payment status
  const isCashPending = paymentStatus === 'unpaid' || paymentStatus === 'cash_pending';
  const isCoffeePassRedemption = paymentStatus === 'coffee_pass';

  return (
    <div className={`min-h-screen flex items-center justify-center p-8 ${
      isCoffeePassRedemption
        ? 'bg-gradient-to-b from-amber-800 to-amber-950'
        : isCashPending 
          ? 'bg-gradient-to-b from-amber-900 to-amber-950' 
          : 'bg-gradient-to-b from-green-900 to-green-950'
    }`} data-testid="kiosk-order-confirmation">
      <div className="text-center text-white max-w-2xl mx-auto">
        {isCoffeePassRedemption ? (
          // COFFEE PASS REDEMPTION - Free coffee, go to counter
          <>
            <div className={`${isAccessible ? 'w-40 h-40' : 'w-32 h-32'} mx-auto mb-8 rounded-full bg-amber-500 flex items-center justify-center`}>
              <Gift className={`${isAccessible ? 'w-20 h-20' : 'w-16 h-16'} animate-bounce`} />
            </div>
            <h1 className={`${isAccessible ? 'text-6xl' : 'text-5xl'} font-bold mb-4`}>Vinaka!</h1>
            <p className={`${isAccessible ? 'text-3xl' : 'text-2xl'} mb-4 text-amber-200`}>Your FREE coffee is being prepared!</p>
            
            {/* Order Number - Extra Large */}
            <div className="bg-white/20 rounded-3xl p-8 mb-8 border-4 border-amber-400">
              <div className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-amber-200 mb-3`}>
                Your Order Number
              </div>
              <div className={`${isAccessible ? 'text-[10rem] leading-none' : 'text-9xl'} font-black text-amber-300`}>
                {orderNumber}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-amber-600/30 rounded-2xl p-6 mb-8 border border-amber-400/50">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-semibold text-amber-200 mb-2`}>
                    What to do next:
                  </p>
                  <ol className={`${isAccessible ? 'text-xl' : 'text-lg'} text-amber-100 space-y-2 list-decimal list-inside`}>
                    <li>Remember your order number: <strong className="text-white text-2xl">{orderNumber}</strong></li>
                    <li>Wait for your number to be called</li>
                    <li>Pick up your free coffee at the counter</li>
                    <li><strong className="text-green-400">No payment needed!</strong></li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="bg-green-500/20 rounded-2xl p-4 mb-6 inline-block">
              <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-green-200`}>
                <Gift className="w-5 h-5 inline mr-2" />
                Bula Coffee Pass redemption recorded!
              </p>
            </div>
          </>
        ) : isCashPending ? (
          // CASH PENDING - Go to cashier
          <>
            <div className={`${isAccessible ? 'w-40 h-40' : 'w-32 h-32'} mx-auto mb-8 rounded-full bg-amber-500 flex items-center justify-center animate-pulse`}>
              <Banknote className={isAccessible ? 'w-20 h-20' : 'w-16 h-16'} />
            </div>
            <h1 className={`${isAccessible ? 'text-6xl' : 'text-5xl'} font-bold mb-4`}>Almost Done!</h1>
            <p className={`${isAccessible ? 'text-3xl' : 'text-2xl'} mb-8`}>Please pay at the cashier</p>
            
            {/* Order Number - Extra Large */}
            <div className="bg-white/20 rounded-3xl p-8 mb-8 border-4 border-amber-400">
              <div className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-amber-200 mb-3`}>
                Tell the cashier your order number:
              </div>
              <div className={`${isAccessible ? 'text-[10rem] leading-none' : 'text-9xl'} font-black text-amber-300`}>
                {orderNumber}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-amber-600/30 rounded-2xl p-6 mb-8 border border-amber-400/50">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-8 h-8 text-amber-300 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-semibold text-amber-200 mb-2`}>
                    Steps to complete your order:
                  </p>
                  <ol className={`${isAccessible ? 'text-xl' : 'text-lg'} text-amber-100 space-y-2 list-decimal list-inside`}>
                    <li>Take note of order number <strong className="text-white">{orderNumber}</strong></li>
                    <li>Go to the cashier counter</li>
                    <li>Tell them your order number</li>
                    <li>Pay <strong className="text-white">${finalTotal.toFixed(2)}</strong> in cash</li>
                    <li>Your order will begin preparation</li>
                  </ol>
                </div>
              </div>
            </div>

            {customer && (
              <div className="bg-amber-500/20 rounded-2xl p-4 mb-6 inline-block">
                <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-amber-200`}>
                  <Gift className="w-5 h-5 inline mr-2" />
                  {Math.floor(finalTotal)} points will be added after payment
                </p>
              </div>
            )}
          </>
        ) : (
          // PAID via M-PAiSA - Order being prepared
          <>
            <div className={`${isAccessible ? 'w-40 h-40' : 'w-32 h-32'} mx-auto mb-8 rounded-full bg-green-500 flex items-center justify-center animate-bounce`}>
              <CheckCircle className={isAccessible ? 'w-20 h-20' : 'w-16 h-16'} />
            </div>
            <h1 className={`${isAccessible ? 'text-7xl' : 'text-6xl'} font-bold mb-4`}>Vinaka!</h1>
            <p className={`${isAccessible ? 'text-3xl' : 'text-2xl'} mb-8`}>Your order is being prepared</p>
            
            {settings.show_order_number_large && (
              <div className="bg-white/10 rounded-3xl p-8 inline-block mb-8">
                <div className={`${isAccessible ? 'text-xl' : 'text-lg'} text-green-200 mb-2`}>Your Order Number</div>
                <div className={`${isAccessible ? 'text-9xl' : 'text-8xl'} font-bold`}>{orderNumber}</div>
              </div>
            )}
            
            <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-green-200 mb-6`}>
              Please wait for your number to be called
            </p>
            
            {customer && (
              <div className="bg-amber-500/20 rounded-2xl p-4 mb-6 inline-block">
                <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-amber-200`}>
                  <Gift className="w-5 h-5 inline mr-2" />
                  Points earned: +{Math.floor(finalTotal)}
                </p>
              </div>
            )}
          </>
        )}
        
        <div className="flex items-center justify-center gap-2 text-gray-300 mt-8">
          <Clock className={`${iconClass} ${countdown <= 5 ? 'animate-pulse text-amber-400' : ''}`} />
          <span className={`${isAccessible ? 'text-lg' : ''} ${countdown <= 5 ? 'text-amber-400 font-bold' : ''}`}>
            Screen will reset in <span className="font-bold text-white">{countdown}</span> seconds
          </span>
        </div>
        <Button 
          onClick={resetKiosk}
          className={`mt-8 ${isAccessible ? 'px-12 py-6 text-2xl' : 'px-8 py-4 text-xl'} bg-white text-gray-900 hover:bg-white/90 ${buttonSizeClass}`}
        >
          Start New Order
        </Button>
      </div>
    </div>
  );
}
